import React, { useState, useEffect, useCallback, useRef } from "react";
import { AngleDisplayOptions } from "./commonHelpers";
import ColorPickerField from "./ColorPickerField";
import { DEFAULT_ZERO_POINT_RADIUS, DEFAULT_LATEX_SYMBOL_SIZE, SymbolType } from "../../pages/AssignmentMatrix/Constants";
import { Button } from "react-md";
import "./style.css"
const inputCompactStyle = { padding: '2px 4px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '3px', width: '100%', boxSizing: 'border-box' };
const rowStyle = { display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '2px 8px' };
const fieldStyle = { display: 'flex', flexDirection: 'column', flex: 1, minWidth: '90px' };
const fieldSmallStyle = { display: 'flex', flexDirection: 'column', flex: 1, minWidth: '55px', maxWidth: '90px' };
const fieldWideStyle = { display: 'flex', flexDirection: 'column', flex: 2, minWidth: '180px' };
const labelStyle = { fontSize: '11px', color: '#555', marginBottom: '2px' };
const inputStyle = { padding: '4px 6px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '3px', width: '100%', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, background: '#fff' };
const selectStyleSmall = { ...inputCompactStyle, background: '#fff', height:'24px', width:'100%' };
const checkboxLabelStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', marginRight: '12px' };
const errorBoxStyle = { color: '#d32f2f', fontSize: '11px', padding: '4px 8px', background: '#fff0f0', borderRadius: '3px', margin: '2px 8px' };
const errorInputStyle = { ...inputStyle, borderColor: '#d32f2f' };
const fieldCompactStyle = { display: 'flex', flexDirection: 'column', minWidth: '40px', maxWidth: '50px' };
const fieldCompactWideStyle = { display: 'flex', flexDirection: 'column', minWidth: '80px', maxWidth: 'none' };
const errorInputCompactStyle = { ...inputCompactStyle, borderColor: '#d32f2f' };
const compactRowStyle = { display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap' };

const NumericInput = ({ value, onCommit, ...props }) => {
    const [localValue, setLocalValue] = useState(value ?? '');
    const isFocusedRef = useRef(false);

    useEffect(() => {
        if (!isFocusedRef.current) {
            setLocalValue(value ?? '');
        }
    }, [value]);

    return (
        <input
            {...props}
            type="number"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onFocus={() => { isFocusedRef.current = true; }}
            onBlur={() => {
                isFocusedRef.current = false;
                if (onCommit) onCommit(localValue);
            }}
        />
    );
};

const onKeyDown = (e) => {
    if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
    }
};

const SFVectorConfigurationCompact = ({
    index,
    variableObj,
    isSnapToGrid,
    onTableRowChange,
}) => {
    const containerRef = useRef(null);
    const [rowErrors, setRowErrors] = useState({});
    const handleErrors = useCallback((key, errs) => {
        setRowErrors(prev => {
            const prevErrs = prev[key] || [];
            if (JSON.stringify(prevErrs) === JSON.stringify(errs)) return prev;
            return { ...prev, [key]: errs };
        });
    }, []);

    const handleContainerKeyDown = useCallback((e) => {
        if (e.key === "Enter") {
            if (e.target.tagName === "SELECT") return;
            e.preventDefault();
            e.stopPropagation();
            if (e.target.type === "checkbox") {
                e.target.click();
            }
            return;
        }
        if (e.key === "Tab" && containerRef.current) {
            const focusable = Array.from(containerRef.current.querySelectorAll(
                'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="button"][tabindex]'
            ));
            const idx = focusable.indexOf(e.target);
            if (idx === -1) return;
            const nextIdx = e.shiftKey ? idx - 1 : idx + 1;
            if (nextIdx >= 0 && nextIdx < focusable.length) {
                e.preventDefault();
                focusable[nextIdx].focus();
            }
        }
    }, []);

    if (!variableObj) return null;


    const allErrors = [...new Set(Object.values(rowErrors).flat())];

    const isCompValid = variableObj.inputType === "components" && (variableObj.variableType === "RANGE" || variableObj.variableType === "RANGE CONTINUOUS" || variableObj.variableType === "LIST");
    const isAngleValid = variableObj.inputType === "angle-magnitude" && (variableObj.variableType === "INT" || variableObj.variableType === "FLOAT" || variableObj.variableType === "LIST");

    return (
        <div ref={containerRef} onKeyDown={handleContainerKeyDown} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
        }}>
            {/* Global error area */}
            {allErrors.length > 0 && (
                <div style={errorBoxStyle}>
                    {allErrors.map((err, i) => <div key={i}>{err}</div>)}
                </div>
            )}
            {/* Row 1: Vector Name, Color, Input Type, Variable Type */}
            <div style={rowStyle}>
                <div style={{
                    ...fieldStyle,
                    width: '240px',
                    flex: 'none',
                }}>
                    <label style={labelStyle}>{"Vector-" + (index + 1)}</label>
                    <input
                        style={inputStyle}
                        type="text"
                        disabled={!variableObj.isStandaloneVector}
                        value={variableObj.name}
                        id={`text-field-${index}`}
                        onChange={(e) => {
                            variableObj.name = e.target.value;
                            onTableRowChange(variableObj);
                        }}
                    />
                </div>
                <div
                    style={{
                        ...fieldStyle,
                        width: '190px',
                        flex: 'none',
                    }}
                >
                    <ColorPickerField
                        index={index}
                        value={variableObj.color}
                        onChange={(value) => {
                            variableObj.color = value;
                            onTableRowChange(variableObj);
                        }}
                    />
                </div>
                <div style={{
                    ...fieldStyle,
                    width: '130px',
                    flex: 'none',
                }}>
                    <label style={labelStyle}>Symbol Type</label>
                    <select style={selectStyle} id={`zero-vector-symbol-${index}`}
                        value={variableObj.latexSymbol}
                        onChange={(e) => {
                            variableObj.latexSymbol = parseInt(e.target.value, 10);
                            // update the x, y min and max to 0 if latexSymbol is not in plane
                            if (variableObj.latexSymbol !== SymbolType.IN_PLANE) {
                                variableObj.xMin = '0';
                                variableObj.xMax = '0';
                                variableObj.yMin = '0';
                                variableObj.yMax = '0';
                                variableObj.vectorBlackListX = "";
                                variableObj.vectorBlackListY = "";
                            }
                            onTableRowChange(variableObj);
                        }}>
                        <option value={SymbolType.IN_PLANE}>In-Plane Arrow</option>
                        <option value={SymbolType.SOLID_DOT}>Solid Dot</option>
                        <option value={SymbolType.OUT_ODOT}>Out (odot)</option>
                        <option value={SymbolType.IN_OTIMES}>In (otimes)</option>
                    </select>
                </div>
                {variableObj.latexSymbol === SymbolType.SOLID_DOT && (
                    <div style={fieldSmallStyle}>
                        <label style={labelStyle}>Dot Radius</label>
                        <NumericInput style={inputStyle}
                            value={variableObj.zeroVectorPointRadius || DEFAULT_ZERO_POINT_RADIUS}
                            id={`radius-${index}`}
                            onCommit={(val) => { variableObj.zeroVectorPointRadius = val; onTableRowChange(variableObj); }} />
                    </div>
                )}
                {(variableObj.latexSymbol === SymbolType.OUT_ODOT || variableObj.latexSymbol === SymbolType.IN_OTIMES) && (
                    <div style={fieldSmallStyle}>
                        <label style={labelStyle}>Symbol Size</label>
                        <NumericInput style={inputStyle}
                            value={variableObj.latexSymbolSize || DEFAULT_LATEX_SYMBOL_SIZE}
                            id={`symbol-size-${index}`}
                            onCommit={(val) => {
                                let parsed = parseInt(val, 10);
                                if (parsed <= 0) { alert("Symbol size cannot be negative"); return; }
                                variableObj.latexSymbolSize = parsed;
                                onTableRowChange(variableObj);
                            }} />
                    </div>
                )}
                <div
                    style={{
                        ...fieldStyle,
                        width: '150px',
                        flex: 'none',
                    }}
                >
                    <label style={labelStyle}>Input Type</label>
                    <select
                        style={selectStyle}
                        id={`variable-type-${index}`}
                        value={variableObj.inputType}
                        onChange={(e) => {
                            variableObj.inputType = e.target.value;
                            if (e.target.value === "components") {
                                variableObj.variableType = "RANGE";
                            } else {
                                variableObj.variableType = "INT";
                            }
                            onTableRowChange(variableObj);
                        }}
                    >
                        <option value="components">components</option>
                        <option value="angle-magnitude">angle-magnitude</option>
                    </select>
                </div>
                <div
                    style={{
                        ...fieldStyle,
                        width: '140px',
                        flex: 'none',
                        display: variableObj.inputType === "components" ? 'block' : 'none',
                    }}
                >
                    <label style={labelStyle}>Variable Type</label>
                    {variableObj.inputType === "components" ? (
                        <select
                            style={selectStyle}
                            id={`variable-type1-${index}`}
                            value={variableObj.variableType === "RANGE" ? "RANGE INTEGER" : variableObj.variableType}
                            onChange={(e) => {
                                let val = e.target.value;
                                if (val === "RANGE INTEGER") val = "RANGE";
                                variableObj.variableType = val;
                                onTableRowChange(variableObj);
                            }}
                        >
                            <option value="RANGE INTEGER">INTEGER</option>
                            <option value="RANGE CONTINUOUS">CONTINUOUS</option>
                            <option value="LIST">LIST</option>
                        </select>
                    ) : (
                        null
                        // <select
                        //     style={selectStyle}
                        //     id={`variable-type1-${index}`}
                        //     value={variableObj.variableType}
                        //     onChange={(e) => {
                        //         variableObj.variableType = e.target.value;
                        //         onTableRowChange(variableObj);
                        //     }}
                        // >
                        //     <option value="INT">RANGE INTEGER</option>
                        //     <option value="FLOAT">RANGE CONTINUOUS</option>
                        //     <option value="LIST">LIST</option>
                        // </select>
                    )}
                </div>
            </div>

            {(isCompValid || isAngleValid) &&
                <OriginXYFields variableObj={variableObj} index={index} onTableRowChange={onTableRowChange} onErrors={handleErrors} />
            }

            {/* Fieldsets */}
            <div style={{ display: "flex", flexWrap: "wrap" }}>
                <fieldset style={{ width: 'fit-content' }}>
                    <legend>Label</legend>
                    <div style={{
                        display: "flex",
                    }}>
                        <label style={checkboxLabelStyle}>
                            <input type="checkbox" id={`show-label-check-${index}`} checked={variableObj.showLabel}
                                onChange={(e) => { variableObj.showLabel = e.target.checked; onTableRowChange(variableObj); }} />
                            Show Label
                        </label>

                        {/* Label Position */}
                        {variableObj.showLabel && (
                            <div style={rowStyle}>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Label Position</label>
                                    <select style={selectStyle} id={`label-position-${index}`}
                                        value={variableObj.labelPositionType || "Automatic"}
                                        onChange={(e) => { variableObj.labelPositionType = e.target.value; onTableRowChange(variableObj); }}>
                                        <option value="Automatic">Automatic</option>
                                        <option value="Automatic Flipped">Automatic Flipped</option>
                                        <option value="Manual">Manual</option>
                                    </select>
                                </div>
                                {(variableObj.labelPositionType === "Manual") && (
                                    <>
                                        <div style={fieldSmallStyle}>
                                            <label style={labelStyle}>Label X</label>
                                            <NumericInput style={inputStyle}
                                                value={variableObj.labelPositionX || variableObj.posX}
                                                id={`label-pos-x-${index}`}
                                                onCommit={(val) => { variableObj.labelPositionX = val; onTableRowChange(variableObj); }} />
                                        </div>
                                        <div style={fieldSmallStyle}>
                                            <label style={labelStyle}>Label Y</label>
                                            <NumericInput style={inputStyle}
                                                value={variableObj.labelPositionY || variableObj.posY}
                                                id={`label-pos-y-${index}`}
                                                onCommit={(val) => { variableObj.labelPositionY = val; onTableRowChange(variableObj); }} />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </fieldset>

                <fieldset style={{ width: 'fit-content' }}>
                    <legend>Legend</legend>
                    <div style={{
                        display: "flex",
                    }}>
                        <label style={checkboxLabelStyle}>
                            <input type="checkbox" id={`show-legend-check-${index}`}
                                checked={(variableObj.showLegend !== null && variableObj.showLegend !== undefined) ? variableObj.showLegend : true}
                                onChange={(e) => { variableObj.showLegend = e.target.checked; onTableRowChange(variableObj); }} />
                            Show in Legend
                        </label>
                        {variableObj.showLegend &&
                            <label style={checkboxLabelStyle}>
                                <input type="checkbox" id={`show-default-checked-check-${index}`}
                                    checked={variableObj.defaultChecked !== null && variableObj.defaultChecked !== undefined && variableObj.defaultChecked !== "" ? variableObj.defaultChecked === "true" : true}
                                    onChange={(e) => { variableObj.defaultChecked = String(e.target.checked); onTableRowChange(variableObj); }} />
                                Default Checked
                            </label>
                        }
                    </div>
                </fieldset>

                <fieldset style={{ width: 'fit-content' }}>
                    <legend>Others</legend>
                    <div style={{
                        display: "flex",
                    }}>
                        <label style={checkboxLabelStyle}>
                            <input type="checkbox" id={`show-components-check-${index}`} checked={variableObj.showComponents}
                                onChange={(e) => { variableObj.showComponents = e.target.checked; onTableRowChange(variableObj); }} />
                            Show Components
                        </label>
                        <label style={checkboxLabelStyle}>
                            <input type="checkbox" id={`auto-origin-check-${index}`} checked={variableObj.autoOrigin}
                                onChange={(e) => { variableObj.autoOrigin = e.target.checked; onTableRowChange(variableObj); }} />
                            Auto Origin
                        </label>
                    </div>
                </fieldset>

                <fieldset style={{ width: 'fit-content' }}>
                    <legend>Angle</legend>
                    <div style={{
                        display: "flex",
                    }}>
                        <label style={checkboxLabelStyle}>
                            <input type="checkbox" id={`show-angle-check-${index}`} checked={variableObj.showAngle}
                                onChange={(e) => { variableObj.showAngle = e.target.checked; onTableRowChange(variableObj); }} />
                            Show Angle
                        </label>
                        <AngleOptions
                            variableObj={variableObj}
                            index={index}
                            onTableRowChange={onTableRowChange}
                            visible={variableObj.showAngle}
                        />
                    </div>
                </fieldset>
            </div>
        </div>
    );
};

/* ── Origin + X/Y Component Fields (compact fieldset layout) ── */
const OriginXYFields = ({ variableObj, index, onTableRowChange, onErrors }) => {
    // X validation state
    const [errorXorigin, setErrorXorigin] = useState(false);
    const [xoriginHelperText, setXoriginHelperText] = useState('');
    const [errorXmin, setErrorXmin] = useState(false);
    const [xminHelperText, setXminHelperText] = useState('');
    const [errorXmax, setErrorXmax] = useState(false);
    const [xmaxHelperText, setXmaxHelperText] = useState('');
    const [errorMagmin, setErrorMagmin] = useState(false);
    const [magminHelperText, setMagminHelperText] = useState('');
    const [errorMagmax, setErrorMagmax] = useState(false);
    const [magmaxHelperText, setMagmaxHelperText] = useState('');

    // Y validation state
    const [errorYorigin, setErrorYorigin] = useState(false);
    const [yoriginHelperText, setYoriginHelperText] = useState('');
    const [errorYmin, setErrorYmin] = useState(false);
    const [yminHelperText, setYminHelperText] = useState('');
    const [errorYmax, setErrorYmax] = useState(false);
    const [ymaxHelperText, setYmaxHelperText] = useState('');
    const [errorAngmin, setErrorAngmin] = useState(false);
    const [angminHelperText, setAngminHelperText] = useState('');
    const [errorAngmax, setErrorAngmax] = useState(false);
    const [angmaxHelperText, setAngmaxHelperText] = useState('');

    // Z validation state
    const [errorZmin, setErrorZmin] = useState(false);
    const [zminHelperText, setZminHelperText] = useState('');
    const [errorZmax, setErrorZmax] = useState(false);
    const [zmaxHelperText, setZmaxHelperText] = useState('');

    useEffect(() => {
        const errs = [...new Set([
            errorXorigin && xoriginHelperText,
            errorXmin && xminHelperText,
            errorXmax && xmaxHelperText,
            errorMagmin && magminHelperText,
            errorMagmax && magmaxHelperText,
        ].filter(Boolean))];
        onErrors('x', errs);
    }, [errorXorigin, xoriginHelperText, errorXmin, xminHelperText, errorXmax, xmaxHelperText, errorMagmin, magminHelperText, errorMagmax, magmaxHelperText, onErrors]);

    useEffect(() => {
        const errs = [...new Set([
            errorYorigin && yoriginHelperText,
            errorYmin && yminHelperText,
            errorYmax && ymaxHelperText,
            errorAngmin && angminHelperText,
            errorAngmax && angmaxHelperText,
        ].filter(Boolean))];
        onErrors('y', errs);
    }, [errorYorigin, yoriginHelperText, errorYmin, yminHelperText, errorYmax, ymaxHelperText, errorAngmin, angminHelperText, errorAngmax, angmaxHelperText, onErrors]);

    useEffect(() => {
        const errs = [...new Set([
            errorZmin && zminHelperText,
            errorZmax && zmaxHelperText,
        ].filter(Boolean))];
        onErrors('z', errs);
    }, [errorZmin, zminHelperText, errorZmax, zmaxHelperText, onErrors]);

    const getfirstInput = (type) => type === "components" ? "vectorBlackListX" : "vectorBlackListMagnitude";
    const getsecondInput = (type) => type === "components" ? "vectorBlackListY" : "vectorBlackListAngle";

    // X validation
    const onXoriginChange = () => {
        if (variableObj.posX != "") {
            if (Number(variableObj.posX) < 0 || Number(variableObj.gridWidthUnit) <= Number(variableObj.posX)) {
                setErrorXorigin(true);
                setXoriginHelperText(`Vector ${variableObj.name} is going outside the grid.`);
            } else {
                setErrorXorigin(false);
                setXoriginHelperText('');
            }
        }
        onXmaxChange();
        onXminChange();
    };

    const onMagminChange = () => {
        if (variableObj.inputType === "angle-magnitude" && variableObj.magMin != "") {
            if (Number(variableObj.magMax) < Number(variableObj.magMin)) {
                setErrorMagmin(true);
                setMagminHelperText(`mag-min cannot be greater than mag-max`);
            } else {
                setErrorMagmin(false);
                setMagminHelperText('');
            }
        }
    };

    const onMagmaxChange = () => {
        if (variableObj.inputType === "angle-magnitude" && variableObj.magMax != "") {
            if (Number(variableObj.magMax) < Number(variableObj.magMin)) {
                setErrorMagmax(true);
                setMagmaxHelperText(`mag-max cannot be less than mag-min`);
            } else {
                setErrorMagmax(false);
                setMagmaxHelperText('');
            }
        }
    };

    const onXminChange = () => {
        if (variableObj.inputType === "components" && variableObj.variableType !== 'LIST' && variableObj.xMin != "") {
            if (Number(variableObj.xMax) < Number(variableObj.xMin)) {
                setErrorXmin(true);
                setXminHelperText(`x-min cannot be greater than x-max`);
            } else if (Number(variableObj.xMin) < -Number(variableObj.posX)) {
                setErrorXmin(true);
                setXminHelperText(`Vector ${variableObj.name} is going outside the grid`);
            } else {
                setErrorXmin(false);
                setXminHelperText('');
            }
        }
    };

    const onXmaxChange = () => {
        if (variableObj.inputType === "components" && variableObj.variableType !== 'LIST' && variableObj.xMax != "") {
            if (Number(variableObj.xMax) < Number(variableObj.xMin)) {
                setErrorXmax(true);
                setXmaxHelperText(`x-max cannot be less than x-min`);
            } else if (Number(variableObj.xMax) > Number(variableObj.gridWidthUnit - variableObj.posX)) {
                setErrorXmax(true);
                setXmaxHelperText(`Vector ${variableObj.name} is going outside the grid`);
            } else {
                setErrorXmax(false);
                setXmaxHelperText('');
            }
        }
    };

    // Y validation
    const onYoriginChange = () => {
        if (variableObj.posY != "") {
            if (Number(variableObj.posY) < 0 || Number(variableObj.gridHeightUnit) <= Number(variableObj.posY)) {
                setErrorYorigin(true);
                setYoriginHelperText(`Vector ${variableObj.name} is going outside the grid.`);
            } else {
                setErrorYorigin(false);
                setYoriginHelperText('');
            }
        }
        onYmaxChange();
        onYminChange();
    };

    const onAngminChange = () => {
        if (variableObj.inputType === "angle-magnitude" && variableObj.angleMin != "") {
            if (Number(variableObj.angleMax) < Number(variableObj.angleMin)) {
                setErrorAngmin(true);
                setAngminHelperText(`angle-min cannot be greater than angle-max`);
            } else {
                setErrorAngmin(false);
                setAngminHelperText('');
            }
        }
    };

    const onAngmaxChange = () => {
        if (variableObj.inputType === "angle-magnitude" && variableObj.angleMax != "") {
            if (Number(variableObj.angleMax) < Number(variableObj.angleMin)) {
                setErrorAngmax(true);
                setAngmaxHelperText(`angle-max cannot be less than angle-min`);
            } else {
                setErrorAngmax(false);
                setAngmaxHelperText('');
            }
        }
    };

    const onYminChange = () => {
        if (variableObj.inputType === "components" && variableObj.variableType !== 'LIST' && variableObj.yMin != "") {
            if (Number(variableObj.yMax) < Number(variableObj.yMin)) {
                setErrorYmin(true);
                setYminHelperText(`y-min cannot be greater than y-max`);
            } else if (Number(variableObj.yMin) < -Number(variableObj.posY)) {
                setErrorYmin(true);
                setYminHelperText(`Vector ${variableObj.name} is going outside the grid`);
            } else {
                setErrorYmin(false);
                setYminHelperText('');
            }
        }
    };

    const onYmaxChange = () => {
        if (variableObj.inputType === "components" && variableObj.variableType !== 'LIST' && variableObj.yMax != "") {
            if (Number(variableObj.yMax) < Number(variableObj.yMin)) {
                setErrorYmax(true);
                setYmaxHelperText(`y-max cannot be less than y-min`);
            } else if (Number(variableObj.yMax) > Number(variableObj.gridHeightUnit - variableObj.posY)) {
                setErrorYmax(true);
                setYmaxHelperText(`Vector ${variableObj.name} is going outside the grid`);
            } else {
                setErrorYmax(false);
                setYmaxHelperText('');
            }
        }
    };

    // Z validation
    const onZminChange = () => {
        if (variableObj.zMin != "") {
            if (Number(variableObj.zMax) < Number(variableObj.zMin)) {
                setErrorZmin(true);
                setZminHelperText(`z-min cannot be greater than z-max`);
            } else {
                setErrorZmin(false);
                setZminHelperText('');
            }
        }
    };

    const onZmaxChange = () => {
        if (variableObj.zMax != "") {
            if (Number(variableObj.zMax) < Number(variableObj.zMin)) {
                setErrorZmax(true);
                setZmaxHelperText(`z-max cannot be less than z-min`);
            } else {
                setErrorZmax(false);
                setZmaxHelperText('');
            }
        }
    };

    const isComponents = variableObj.inputType === "components";
    const isRange = variableObj.variableType === "RANGE" || variableObj.variableType === "INT" || variableObj.variableType === "RANGE CONTINUOUS" || variableObj.variableType === "FLOAT";
    const isInPlane = variableObj.latexSymbol === SymbolType.IN_PLANE;
    const isOutOrIn = variableObj.latexSymbol === SymbolType.OUT_ODOT || variableObj.latexSymbol === SymbolType.IN_OTIMES;

    // magVariableType / angleVariableType default to "INT" when unset (same as the parser)
    const isMagRange = (variableObj.magVariableType || "INT") !== "LIST";
    const isAngleRange = (variableObj.angleVariableType || "INT") !== "LIST";

    // In angle-magnitude mode only the per-field type decides; variableType is always INT there
    const showFirstRange = isComponents ? isRange : isMagRange;
    const showSecondRange = isComponents ? isRange : isAngleRange;

    return (
        <div style={{ display: "flex", flexWrap: "wrap" }}>
            {/* Origin fieldset */}
            <fieldset style={{ width: 'fit-content' }}>
                <legend>Origin</legend>
                <div style={compactRowStyle}>
                    <div style={fieldCompactStyle}>
                        <label style={labelStyle}>x</label>
                        <NumericInput
                            style={errorXorigin ? errorInputCompactStyle : inputCompactStyle}
                            disabled={variableObj.autoOrigin}
                            value={variableObj.posX || ""}
                            id={`text-x-${index}`}
                            onCommit={(val) => { variableObj.posX = val; onTableRowChange(variableObj); onXoriginChange(); }}
                            onKeyDown={onKeyDown}
                        />
                    </div>
                    <div style={fieldCompactStyle}>
                        <label style={labelStyle}>y</label>
                        <NumericInput
                            style={errorYorigin ? errorInputCompactStyle : inputCompactStyle}
                            disabled={variableObj.autoOrigin}
                            value={variableObj.posY || ""}
                            id={`text-y-${index}`}
                            onCommit={(val) => { variableObj.posY = val; onTableRowChange(variableObj); onYoriginChange(); }}
                            onKeyDown={onKeyDown}
                        />
                    </div>
                </div>
            </fieldset>

            {/* X Component / Magnitude fieldset */}
            {isInPlane && (
                <fieldset style={{ width: 'fit-content' }}>
                    <legend>{isComponents ? "X Component" : "Magnitude"}</legend>
                    <div style={compactRowStyle}>
                        {isComponents ? (
                            isRange ? (
                                <>
                                    <div style={fieldCompactStyle}>
                                        <label style={labelStyle}>min</label>
                                        <NumericInput style={errorXmin ? errorInputCompactStyle : inputCompactStyle} value={variableObj.xMin} id={`text-xmin-${index}`}
                                            onCommit={(val) => { variableObj.xMin = val; onTableRowChange(variableObj); onXminChange(); onXmaxChange(); }}
                                            onKeyDown={onKeyDown} />
                                    </div>
                                    <div style={fieldCompactStyle}>
                                        <label style={labelStyle}>max</label>
                                        <NumericInput style={errorXmax ? errorInputCompactStyle : inputCompactStyle} value={variableObj.xMax} id={`text-xmax-${index}`}
                                            onCommit={(val) => { variableObj.xMax = val; onTableRowChange(variableObj); onXmaxChange(); onXminChange(); }}
                                            onKeyDown={onKeyDown} />
                                    </div>
                                </>
                            ) : (
                                <div style={fieldCompactWideStyle}>
                                    <label style={labelStyle}>list</label>
                                    <input style={inputCompactStyle} type="text" placeholder='e.g. 1,2,3'
                                        value={variableObj.xList || ""} id={`list-values-xList`}
                                        onChange={(e) => { variableObj.xList = e.target.value; onTableRowChange(variableObj); }}
                                        onKeyDown={onKeyDown} />
                                </div>
                            )
                        ) : (
                            <React.Fragment>
                                <div style={{maxWidth:'100px'}}>
                                    <label style={labelStyle}>Var. Type</label>
                                    <select
                                        style={selectStyleSmall}
                                        id={`mag-variable-type-${index}`}
                                        value={variableObj.magVariableType ? variableObj.magVariableType : "INT"}
                                        onChange={(e) => {
                                            variableObj.magVariableType = e.target.value;
                                            onTableRowChange(variableObj);
                                        }}
                                    >
                                        {/* <option value=""></option> */}
                                        <option value="INT">INTEGER</option>
                                        <option value="FLOAT">CONTINUOUS</option>
                                        <option value="LIST">LIST</option>
                                    </select>
                                </div>

                                {
                                    showFirstRange ? (
                                        <>
                                            <div style={fieldCompactStyle}>
                                                <label style={labelStyle}>min</label>
                                                <NumericInput style={errorMagmin ? errorInputCompactStyle : inputCompactStyle} value={variableObj.magMin} id={`mag-min-${index}`}
                                                    onCommit={(val) => { variableObj.magMin = val; onTableRowChange(variableObj); onMagminChange(); }}
                                                    onKeyDown={onKeyDown} />
                                            </div>
                                            <div style={fieldCompactStyle}>
                                                <label style={labelStyle}>max</label>
                                                <NumericInput style={errorMagmax ? errorInputCompactStyle : inputCompactStyle} value={variableObj.magMax} id={`mag-max-${index}`}
                                                    onCommit={(val) => { variableObj.magMax = val; onTableRowChange(variableObj); onMagmaxChange(); }}
                                                    onKeyDown={onKeyDown} />
                                            </div>
                                        </>
                                    ) : (
                                        <div style={fieldCompactWideStyle}>
                                            <label style={labelStyle}>list</label>
                                            <input style={inputCompactStyle} type="text" placeholder='e.g. 1,2,3'
                                                value={variableObj.magList || ""} id={`list-values-magList`}
                                                onChange={(e) => { variableObj.magList = e.target.value; onTableRowChange(variableObj); }}
                                                onKeyDown={onKeyDown} />
                                        </div>
                                    )
                                }

                            </React.Fragment>
                        )}
                        {showFirstRange && (
                            <div style={fieldCompactWideStyle}>
                                <label style={labelStyle}>excluded list</label>
                                <input style={inputCompactStyle} type="text" placeholder="e.g. 1,2"
                                    value={variableObj[getfirstInput(variableObj.inputType)] || ""} id={`text-black-list-x-${index}`}
                                    onChange={(e) => { variableObj[getfirstInput(variableObj.inputType)] = e.target.value; onTableRowChange(variableObj); }}
                                    onKeyDown={onKeyDown} />
                            </div>
                        )}
                    </div>
                </fieldset>
            )}

            {/* Y Component / Angle fieldset */}
            {isInPlane && (
                <fieldset style={{ width: 'fit-content' }}>
                    <legend>{isComponents ? "Y Component" : "Angle"}</legend>
                    <div style={compactRowStyle}>
                        {isComponents ? (
                            isRange ? (
                                <>
                                    <div style={fieldCompactStyle}>
                                        <label style={labelStyle}>min</label>
                                        <NumericInput style={errorYmin ? errorInputCompactStyle : inputCompactStyle} value={variableObj.yMin} id={`text-ymin-${index}`}
                                            onCommit={(val) => { variableObj.yMin = val; onTableRowChange(variableObj); onYminChange(); onYmaxChange(); }}
                                            onKeyDown={onKeyDown} />
                                    </div>
                                    <div style={fieldCompactStyle}>
                                        <label style={labelStyle}>max</label>
                                        <NumericInput style={errorYmax ? errorInputCompactStyle : inputCompactStyle} value={variableObj.yMax} id={`text-ymax-${index}`}
                                            onCommit={(val) => { variableObj.yMax = val; onTableRowChange(variableObj); onYmaxChange(); onYminChange(); }}
                                            onKeyDown={onKeyDown} />
                                    </div>
                                </>
                            ) : (
                                <div style={fieldCompactWideStyle}>
                                    <label style={labelStyle}>list</label>
                                    <input style={inputCompactStyle} type="text" placeholder='e.g. 1,2,3'
                                        value={variableObj.yList || ""} id={`list-values-yList`}
                                        onChange={(e) => { variableObj.yList = e.target.value; onTableRowChange(variableObj); }}
                                        onKeyDown={onKeyDown} />
                                </div>
                            )
                        ) : (
                            <React.Fragment>
                                <div style={{maxWidth:'100px'}}>
                                    <label style={labelStyle}>Var. Type</label>
                                    <select
                                        style={selectStyleSmall}
                                        id={`angle-variable-type-${index}`}
                                        value={variableObj.angleVariableType ? variableObj.angleVariableType : "INT"}
                                        onChange={(e) => {
                                            variableObj.angleVariableType = e.target.value;
                                            onTableRowChange(variableObj);
                                        }}
                                    >
                                        {/* <option value=""></option> */}
                                        <option value="INT">INTEGER</option>
                                        <option value="FLOAT">CONTINUOUS</option>
                                        <option value="LIST">LIST</option>
                                    </select>
                                </div>
                                {
                                    showSecondRange ? (
                                        <>
                                            <div style={fieldCompactStyle}>
                                                <label style={labelStyle}>min</label>
                                                <NumericInput style={errorAngmin ? errorInputCompactStyle : inputCompactStyle} value={variableObj.angleMin} id={`angle-min-${index}`}
                                                    onCommit={(val) => { variableObj.angleMin = val; onTableRowChange(variableObj); onAngminChange(); }}
                                                    onKeyDown={onKeyDown} />
                                            </div>
                                            <div style={fieldCompactStyle}>
                                                <label style={labelStyle}>max</label>
                                                <NumericInput style={errorAngmax ? errorInputCompactStyle : inputCompactStyle} value={variableObj.angleMax} id={`angle-max-${index}`}
                                                    onCommit={(val) => { variableObj.angleMax = val; onTableRowChange(variableObj); onAngmaxChange(); }}
                                                    onKeyDown={onKeyDown} />
                                            </div>
                                        </>
                                    ) : (
                                        <div style={fieldCompactWideStyle}>
                                            <label style={labelStyle}>list</label>
                                            <input style={inputCompactStyle} type="text" placeholder='e.g. 1,2,3'
                                                value={variableObj.angleList || ""} id={`list-values-angleList`}
                                                onChange={(e) => { variableObj.angleList = e.target.value; onTableRowChange(variableObj); }}
                                                onKeyDown={onKeyDown} />
                                        </div>
                                    )
                                }
                            </React.Fragment>

                        )}
                        {showSecondRange && (
                            <div style={fieldCompactWideStyle}>
                                <label style={labelStyle}>excluded list</label>
                                <input style={inputCompactStyle} type="text" placeholder="e.g. 1,2"
                                    value={variableObj[getsecondInput(variableObj.inputType)] || ""} id={`text-black-list-y-${index}`}
                                    onChange={(e) => { variableObj[getsecondInput(variableObj.inputType)] = e.target.value; onTableRowChange(variableObj); }}
                                    onKeyDown={onKeyDown} />
                            </div>
                        )}
                    </div>
                </fieldset>
            )}

            {/* Z Component fieldset (OUT_ODOT / IN_OTIMES only) */}
            {isOutOrIn && (
                <fieldset style={{ width: 'fit-content' }}>
                    <legend>Z Component</legend>
                    <div style={compactRowStyle}>
                        {isRange ? (
                            <>
                                <div style={fieldCompactStyle}>
                                    <label style={labelStyle}>min</label>
                                    <NumericInput style={errorZmin ? errorInputCompactStyle : inputCompactStyle} value={variableObj.zMin} id={`text-zmin-${index}`}
                                        onCommit={(val) => { variableObj.zMin = val; onTableRowChange(variableObj); onZminChange(); onZmaxChange(); }}
                                        onKeyDown={onKeyDown} />
                                </div>
                                <div style={fieldCompactStyle}>
                                    <label style={labelStyle}>max</label>
                                    <NumericInput style={errorZmax ? errorInputCompactStyle : inputCompactStyle} value={variableObj.zMax} id={`text-zmax-${index}`}
                                        onCommit={(val) => { variableObj.zMax = val; onTableRowChange(variableObj); onZmaxChange(); onZminChange(); }}
                                        onKeyDown={onKeyDown} />
                                </div>
                                <div style={fieldCompactWideStyle}>
                                    <label style={labelStyle}>excl.</label>
                                    <input style={inputCompactStyle} type="text" placeholder="e.g. 1,2"
                                        value={variableObj.vectorBlackListZ || ""} id={`text-black-list-z-${index}`}
                                        onChange={(e) => { variableObj.vectorBlackListZ = e.target.value; onTableRowChange(variableObj); }}
                                        onKeyDown={onKeyDown} />
                                </div>
                            </>
                        ) : (
                            <div style={fieldCompactWideStyle}>
                                <label style={labelStyle}>list</label>
                                <input style={inputCompactStyle} type="text" placeholder='e.g. 1,2,3'
                                    value={variableObj.zList || ""} id={`list-values-zList`}
                                    onChange={(e) => { variableObj.zList = e.target.value; onTableRowChange(variableObj); }}
                                    onKeyDown={onKeyDown} />
                            </div>
                        )}
                    </div>
                </fieldset>
            )}
        </div>
    );
};

/* ── Angle Options ── */
const AngleOptions = ({ variableObj, index, onTableRowChange, visible }) => {
    if (!visible) return null;

    let angleOptions = variableObj ? variableObj.angleOptions : {};
    if (!angleOptions) angleOptions = {};

    const getHelpText = () => {
        return (
            <p className="custom-tooltip-text">
                {AngleDisplayOptions.map((array, idx) => (
                    <p key={idx}>Option{idx + 1}: {array.label}</p>
                ))}
            </p>
        );
    };

    return (
        <div style={rowStyle}>
            <div style={fieldStyle}>
                <label style={labelStyle}>Angle Display Options</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <select style={{ ...selectStyle, flex: 1 }} id={`angle-option-${index}`} value={angleOptions.type || ""}
                        onChange={(e) => {
                            const val = Number(e.target.value);
                            variableObj.angleOptions = { ...angleOptions, type: val };
                            onTableRowChange(variableObj);
                        }}>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                    </select>
                    <Button
                        className="nocqcStyle"
                        icon
                        tooltipLabel={getHelpText()}
                    >
                        help
                    </Button>
                </div>
            </div>
            <div style={fieldStyle}>
                <label style={labelStyle}>Angle offset</label>
                <NumericInput style={inputStyle} placeholder="Offset" value={angleOptions.angleOffset || ""} id={`angle-offset-${index}`}
                    onCommit={(val) => {
                        variableObj.angleOptions = { ...angleOptions, angleOffset: val ? parseFloat(val) : '' };
                        onTableRowChange(variableObj);
                    }}
                    onKeyDown={onKeyDown} />
            </div>
            <div style={{ ...fieldStyle, justifyContent: 'center', minWidth: 'auto' }}>
                <label style={checkboxLabelStyle}>
                    <input type="checkbox" id={`show-angle-clockwise-${index}`} checked={angleOptions.clockwise || false}
                        onChange={(e) => {
                            variableObj.angleOptions = { ...angleOptions, clockwise: e.target.checked };
                            onTableRowChange(variableObj);
                        }} />
                    Clockwise
                </label>
            </div>
        </div>
    );
};

export default SFVectorConfigurationCompact;
